<script setup>
/**
 * Client Consent Panel (admin)
 * Shows and edits the two consents on a ClientsV2 doc:
 *   - marketing: 3-state (approved / declined / pending) + source (form / phone)
 *   - siteTerms: accepted or not, with a one-click "mark accepted"
 * Manual entry is how the admin records answers taken from the B2B join form when the
 * client is created (decision 2026-08-27 — no automatic form→client sync).
 */
import { ref, computed } from 'vue'
import { useAuthStore } from '@app/store/auth.js'
import { updateClient, recordMarketingConsent } from '@core/services/clients.js'
import { useToast } from '@core/composables/useToast.js'
import { formatDateTime } from '@core/utils/formatting.js'
import {
  getMarketingState,
  hasAcceptedSiteTerms,
  getUnsubscribedAt,
  buildSiteTermsPayload,
} from '@core/utils/consent.js'
import { CONSENT_SOURCE, CONSENT_SOURCE_LABELS } from '@core/config/consentConstants.js'

const props = defineProps({
  client: { type: Object, required: true },
})

const authStore = useAuthStore()
const { success: toastSuccess, error: toastError } = useToast()

const saving = ref(false)
const source = ref(CONSENT_SOURCE.B2B_FORM)

const marketingState = computed(() => getMarketingState(props.client))
const marketingMeta = computed(() => props.client.marketing || null)
// Kept even after a re-approval — the record of when the customer opted out via WhatsApp
const unsubscribedAt = computed(() => getUnsubscribedAt(props.client))
const termsAccepted = computed(() => hasAcceptedSiteTerms(props.client))
const termsMeta = computed(() => props.client.siteTerms || null)

function sourceLabel(src) {
  return CONSENT_SOURCE_LABELS[src] || src || '—'
}

function metaLine(meta, dateKey) {
  if (!meta) return ''
  const parts = [sourceLabel(meta.source)]
  const date = meta[dateKey]
  if (date) parts.push(formatDateTime(date))
  return parts.join(' · ')
}

async function setMarketing(event) {
  const value = event.target.value
  if (value !== 'true' && value !== 'false') return
  if (saving.value) return
  saving.value = true
  try {
    await recordMarketingConsent({
      clientId: props.client.id,
      consent: value === 'true',
      uid: authStore.userId,
      source: source.value,
      previousMarketing: props.client.marketing || null,
    })
    toastSuccess('אישור דיוור עודכן')
  } catch (err) {
    console.error('[ClientConsentPanel] marketing update failed', err)
    toastError('שגיאה בעדכון אישור דיוור')
    // Re-sync the native select with the (unchanged) doc state
    event.target.value =
      marketingState.value === 'pending' ? 'pending' : String(marketingState.value === 'approved')
  } finally {
    saving.value = false
  }
}

async function markTermsAccepted() {
  if (saving.value) return
  saving.value = true
  try {
    await updateClient(props.client.id, {
      siteTerms: buildSiteTermsPayload({ uid: authStore.userId, source: source.value }),
    })
    toastSuccess('אישור תקנון נרשם')
  } catch (err) {
    console.error('[ClientConsentPanel] siteTerms update failed', err)
    toastError('שגיאה ברישום אישור תקנון')
  } finally {
    saving.value = false
  }
}
</script>

<template>
  <div class="detail-section consent-panel">
    <h4 class="detail-section-title">הסכמות</h4>
    <div class="detail-grid">
      <div class="detail-item">
        <span class="detail-label">מקור הרישום הידני</span>
        <select v-model="source" class="consent-select" :disabled="saving">
          <option :value="CONSENT_SOURCE.B2B_FORM">
            {{ sourceLabel(CONSENT_SOURCE.B2B_FORM) }}
          </option>
          <option :value="CONSENT_SOURCE.ADMIN">{{ sourceLabel(CONSENT_SOURCE.ADMIN) }}</option>
        </select>
      </div>

      <div class="detail-item">
        <span class="detail-label">דיוור ותוכן שיווקי</span>
        <select
          class="consent-select"
          :class="`state-${marketingState}`"
          :value="marketingState === 'pending' ? 'pending' : String(marketingState === 'approved')"
          :disabled="saving"
          @change="setMarketing"
        >
          <option v-if="marketingState === 'pending'" value="pending" disabled>טרם נענה</option>
          <option value="true">מאושר</option>
          <option value="false">לא מאושר</option>
        </select>
        <span v-if="marketingMeta?.answered" class="consent-meta">
          {{ metaLine(marketingMeta, 'answeredAt') }}
        </span>
        <span v-if="unsubscribedAt" class="consent-meta consent-meta-unsubscribed">
          הוסר מרשימת התפוצה בוואטסאפ · {{ formatDateTime(unsubscribedAt) }}
        </span>
      </div>

      <div class="detail-item">
        <span class="detail-label">תקנון האתר</span>
        <template v-if="termsAccepted">
          <span class="consent-badge accepted">מאושר</span>
          <span class="consent-meta">{{ metaLine(termsMeta, 'acceptedAt') }}</span>
        </template>
        <button v-else class="consent-mark-btn" :disabled="saving" @click="markTermsAccepted">
          סמן כמאושר
        </button>
      </div>
    </div>
  </div>
</template>

<style scoped>
.consent-panel {
  background: var(--bg-primary);
  border-radius: var(--border-radius);
  padding: var(--spacing-md);
  border: 1px solid var(--border-color);
}

.detail-section-title {
  font-size: var(--font-size-sm);
  font-weight: var(--font-weight-semibold);
  color: var(--text-primary);
  margin: 0 0 var(--spacing-md);
  padding-bottom: var(--spacing-sm);
  border-bottom: 1px solid var(--border-color);
}

.detail-grid {
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(180px, 1fr));
  gap: var(--spacing-md);
}

.detail-item {
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  gap: 4px;
}

.detail-label {
  font-size: 11px;
  font-weight: var(--font-weight-medium);
  color: var(--text-muted);
  text-transform: uppercase;
}

.consent-select {
  padding: 6px 10px;
  border: 1px solid var(--border-color);
  border-radius: var(--border-radius);
  background: var(--bg-primary);
  color: var(--text-primary);
  font-family: var(--font-family);
  font-size: var(--font-size-sm);
  cursor: pointer;
}

.consent-select.state-approved {
  border-color: var(--color-success);
  color: var(--color-success);
}

.consent-select.state-declined {
  border-color: var(--color-error);
  color: var(--color-error);
}

.consent-select.state-pending {
  border-color: var(--color-warning);
  color: var(--color-warning);
}

.consent-select:disabled,
.consent-mark-btn:disabled {
  opacity: 0.6;
  cursor: not-allowed;
}

.consent-meta {
  display: block;
  margin-top: 4px;
  font-size: 11px;
  color: var(--text-muted);
}

.consent-meta-unsubscribed {
  color: var(--color-error);
  font-weight: var(--font-weight-medium);
}

.consent-badge {
  display: inline-block;
  padding: 4px 8px;
  border-radius: var(--border-radius-full);
  font-size: 11px;
  font-weight: var(--font-weight-medium);
}

.consent-badge.accepted {
  background: var(--color-success-light);
  color: var(--color-success);
}

.consent-mark-btn {
  padding: 6px 12px;
  border: 1px solid var(--color-primary);
  border-radius: var(--border-radius);
  background: transparent;
  color: var(--color-primary);
  font-family: var(--font-family);
  font-size: var(--font-size-sm);
  cursor: pointer;
  transition: all var(--transition-fast);
}

.consent-mark-btn:hover:not(:disabled) {
  background: var(--color-primary);
  color: var(--text-inverse);
}
</style>
